import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const UpgradeModal = ({ isOpen, onClose, feature, title, description }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  if (!isOpen) return null;

  const featureDescriptions = {
    'pdf': {
      title: 'PDF Upload',
      description: 'Upload entire textbooks and documents to generate quizzes instantly.',
      icon: '📄'
    },
    'url': {
      title: 'URL/YouTube Import',
      description: 'Turn any webpage or YouTube video into a quiz with one click.',
      icon: '🔗'
    },
    'image': {
      title: 'Image Upload',
      description: 'Snap a photo of your notes and let AI create quizzes from it.',
      icon: '🖼️'
    },
    'audio': {
      title: 'Audio/Video Upload',
      description: 'Convert lectures and podcasts into study quizzes.',
      icon: '🎵'
    },
    'solver': {
      title: 'AI Question Solver',
      description: 'Get step-by-step solutions to any question you\'re stuck on.',
      icon: '🧠'
    },
    'history': {
      title: 'Quiz History & Analytics',
      description: 'Track your progress and see detailed performance insights.',
      icon: '📊'
    },
    'mistakes': {
      title: 'Practice My Mistakes',
      description: 'AI identifies your weak areas and creates targeted practice quizzes.',
      icon: '🎯'
    },
    'default': {
      title: 'Premium Feature',
      description: 'This feature is part of the Premium plan.',
      icon: '⭐'
    }
  };

  const featureInfo = featureDescriptions[feature] || featureDescriptions['default'];
  const displayTitle = title || featureInfo.title;
  const displayDescription = description || featureInfo.description;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-center">
          <div className="text-5xl mb-3">{featureInfo.icon}</div>
          <h2 className="text-2xl font-bold text-white mb-1">
            🔒 {displayTitle}
          </h2>
          <p className="text-indigo-100 text-sm">Premium Feature</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {displayDescription}
          </p>

          {/* Benefits list */}
          <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-indigo-50'}`}>
            <p className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Upgrade to Premium and unlock:
            </p>
            <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>Unlimited quiz generation</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>All input types (PDF, URL, Image, Audio)</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>AI Question Solver</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>Practice My Mistakes feature</span>
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span>Full analytics & progress tracking</span>
              </li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <button
            onClick={() => navigate('/pricing')}
            className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center"
          >
            <span className="mr-2">⚡</span>
            Upgrade Now
          </button>

          <button
            onClick={onClose}
            className={`w-full mt-3 py-2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
